/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Amount, Contact } from '@api';
import { Stripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { attachScript, attachScriptHTML } from 'src/js/common/utils/DomUtils';
import CreditCardHeader from 'src/js/enrol/containers/payment/components/CreditCardHeader';
import EwayPaymentComp from 'src/js/enrol/containers/payment/components/EwayPaymentComp';
import { PayerAdd } from 'src/js/enrol/containers/payment/components/PayerAdd';
import { PayerSelect } from 'src/js/enrol/containers/payment/components/PayerSelect';
import SquarePaymentComp from 'src/js/enrol/containers/payment/components/SquarePaymentComp';
import StripePaymentComp from 'src/js/enrol/containers/payment/components/StripePaymentComp';
import WindcavePaymentComp from 'src/js/enrol/containers/payment/components/WindcavePaymentComp';
import { PaymentGateways } from 'src/js/model/checkout/payment/PaymentGateways';

interface Props {
  contacts: Contact[];
  amount: Amount;
  onSetPayer?: (id: string) => void;
  onAddPayer?: () => any;
  onAddCompany?: () => any;
  payerId?: string;
  paymentGatewayClientKey?: string;
  voucherPayerEnabled?: boolean;
  disabled?: boolean;
  iframeUrl: string;
  dispatch: Dispatch<any>;
  paymentGatewayType?: PaymentGateways;
  checkoutProcessSquareCcPayment: (creditCardToken: string, verificationToken: string) => void;
  checkoutProcessStripeCCPayment: (stripePaymentMethodId: string, stripe: Stripe) => void;
  checkoutProcessEwayCCPayment: (code: string) => void;
  checkoutCompleteWindcaveCcPayment: (sessionId: string) => void;
}

export function PaymentCompResolver(
  {
    contacts,
    amount,
    onSetPayer,
    onAddPayer,
    onAddCompany,
    payerId,
    voucherPayerEnabled,
    disabled,
    iframeUrl,
    checkoutProcessSquareCcPayment,
    checkoutProcessStripeCCPayment,
    checkoutProcessEwayCCPayment,
    checkoutCompleteWindcaveCcPayment,
    dispatch,
    paymentGatewayType
  }: Props) {

  const [scriptAttached, setScriptAttached] = useState(false)

  useEffect(() => {
    attachScript('//vice-prod.sdiapi.com/vice_loader/93bcb219-23a6-4426-806c-d32eb914c411/baf2d5c8-68fa-49c7-9379-49f248dc8fb3', null, 'top')
    attachScriptHTML('vice={config:{viceAccountId:\'93bcb219-23a6-4426-806c-d32eb914c411\',viceSiteId:\'baf2d5c8-68fa-49c7-9379-49f248dc8fb3\'}}', 'top')
  }, [])

  useEffect(() => {
    if (paymentGatewayType) {
      const onLoad = () => {
        setScriptAttached(true)
      };

      switch (paymentGatewayType) {
        case 'EWAY':
        case 'EWAY_TEST': {
          [
            'https://secure.ewaypayments.com/scripts/eWAY.min.js',
            // Switch between production and test environments
            paymentGatewayType === 'EWAY' ? 'https://static.assets.eway.io/cerberus/6.6.2.54470/assets/sdk/cerberus.bundle.js' : 'https://static.assets.eway.io/cerberus/6.6.2.54470/assets/sdk/cerberus-sandbox.bundle.js'
          ].forEach((url,index,self) => {
            attachScript(url, index === self.length - 1 && onLoad)
          });
          break;
        }
        case 'SQUARE':
        case 'SQUARE_TEST': {
          attachScript('https://sandbox.web.squarecdn.com/v1/square.js', onLoad);
          break;
        }
        default: {
          onLoad();
        }
      }
    }
  }, [paymentGatewayType]);


  const getPaymentComp = () => {
    switch (paymentGatewayType) {
      case 'EWAY':
      case 'EWAY_TEST':
        return  <EwayPaymentComp
          amount={amount}
          checkoutProcessEwayCCPayment={checkoutProcessEwayCCPayment}
          disabled={disabled}
          dispatch={dispatch}
        />
      case 'SQUARE_TEST':
      case 'SQUARE':
        return <SquarePaymentComp
          checkoutProcessSquareCcPayment={checkoutProcessSquareCcPayment}
          contacts={contacts}
          dispatch={dispatch}
          amount={amount}
          disabled={disabled}
          payerId={payerId}
        />;
      case 'STRIPE_TEST':
      case 'STRIPE':
        return <StripePaymentComp
          amount={amount}
          contacts={contacts}
          payerId={payerId}
          onSetPayer={onSetPayer}
          onAddPayer={onAddPayer}
          onAddCompany={onAddCompany}
          checkoutProcessStripeCCPayment={checkoutProcessStripeCCPayment}
          disabled={disabled}
          dispatch={dispatch}
        />
      case 'WINDCAVE':
      case 'WINDCAVE_TEST':
      case 'TEST':
        return <WindcavePaymentComp
          amount={amount}
          dispatch={dispatch}
          disabled={disabled}
          iframeUrl={iframeUrl}
          checkoutCompleteWindcaveCcPayment={checkoutCompleteWindcaveCcPayment}
        />
      default:
        return null;
    }
  }

  return <div id="credit-card" className="single-tab active">
    <CreditCardHeader />
    <div className="enrolmentsSelected">
      <fieldset className="field-wrapper credit-card-v2-wrapper">
        <div className="form-details" >
          <p>
            <label>Pay now</label>
            <span style={{bottom: '-4px'}}>
                ${Number(amount.ccPayment).toFixed(2)}
              </span>
          </p>
          <PayerSelect
            contacts={contacts}
            payer={contacts.find(c => c.id === payerId)}
            onChange={onSetPayer}
            disabled={voucherPayerEnabled}
          />
          <PayerAdd
            onAddPayer={onAddPayer}
            onAddCompany={onAddCompany}
            disabled={voucherPayerEnabled}
          />
          <div style={disabled ? {
            opacity: 0.6,
            pointerEvents: 'none',
          }: null}>
            {scriptAttached && getPaymentComp()}
          </div>
        </div>
      </fieldset>
    </div>
  </div>
}